import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { DecisionBucket } from "@/types";

const schema = z.object({ motivo: z.string().trim().max(400, "Máximo 400 caracteres") });
type FormValues = z.infer<typeof schema>;

const ETIQUETAS: Record<DecisionBucket, string> = {
  postular: "Postular",
  revisar: "Revisar",
  no_postular: "No postular",
};

export const bucketLabel = (b: DecisionBucket) => ETIQUETAS[b] ?? b;

/**
 * Diálogo de decisión con captura de motivo. Vive aparte de DecisionesPage
 * porque el detalle de una oportunidad decide con el mismo endpoint y las
 * mismas reglas — `no_postular` exige motivo de 5+ caracteres, validado
 * también en el backend (DecideDto).
 */
export function DecisionDialog({
  pending,
  submitting,
  onCancel,
  onConfirm,
}: {
  /** Proceso sobre el que se decide; `nombre` es sólo para mostrar. */
  pending: { nombre: string; bucket: DecisionBucket } | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (motivo: string) => void;
}) {
  const requiereMotivo = pending?.bucket === "no_postular";
  const form = useForm<FormValues>({
    resolver: zodResolver(
      requiereMotivo
        ? schema.extend({ motivo: z.string().trim().min(5, "Explica por qué se descarta") })
        : schema,
    ),
    defaultValues: { motivo: "" },
    mode: "onBlur",
  });

  return (
    <Dialog
      open={Boolean(pending)}
      onOpenChange={(open) => {
        if (!open) {
          form.reset({ motivo: "" });
          onCancel();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mover a {pending ? bucketLabel(pending.bucket) : ""}</DialogTitle>
          <DialogDescription>
            {pending?.nombre}
            {requiereMotivo
              ? " · Deja registro de por qué se descarta; esta decisión queda auditada."
              : " · La decisión queda registrada y mueve el pipeline del CRM."}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit((values) => onConfirm(values.motivo))}
          className="space-y-3"
        >
          <div>
            <label htmlFor="motivo" className="text-xs text-muted-foreground">
              Motivo {requiereMotivo ? "(obligatorio)" : "(opcional)"}
            </label>
            <Textarea
              id="motivo"
              rows={3}
              placeholder="Ej: fuera de nuestra capacidad técnica para el plazo exigido"
              {...form.register("motivo")}
            />
            {form.formState.errors.motivo && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.motivo.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={requiereMotivo ? "destructive" : "default"}
              disabled={submitting}
            >
              {submitting && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Confirmar decisión
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
